import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { applyLvChange } from '../api/project-lv.api'
import { LV_FELD, lvWert } from './lv-labels'
import { ArrowDown, ArrowUp, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ClipboardPaste, Copy, Filter, Plus, Scissors, Timer, Trash2 } from 'lucide-react'
import {
  SALES_LINE_ORIGIN_LABELS,
  SALES_LINE_STATES,
  SALES_LINE_STATE_LABELS,
  SALES_LINE_TYPE_LABELS,
  SALES_LINE_TYPES,
  erfuelltRecht,
  verkaufZeilenSumme,
  type SalesLineState,
  type SalesLineType,
} from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/lib/auth.store'
import { eingabeOffen, mitStand, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Gruppe, type Speicherstelle, type Zustand } from '@/lib/speichern'
import { KlappUmschalter, useKlappModus, zeileOffen, type KlappModus } from '@/components/klappmodus'
import { FilterBar } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { RAND_ZEIGER, ZiehBereich, useRandMarkieren, useZeileGreifen, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { GeldFeld } from '@/components/geldfeld'
import { fetchClipboard } from '../api/projects-phase-d.api'
import { fetchUnits } from '../api/project-bom.api'
import {
  copySalesLines,
  createSalesLine,
  deleteSalesLine,
  fetchSalesLines,
  pasteSalesLines,
  reorderSalesLines,
  searchArticles,
  splitSalesLine,
  type SalesLine,
  type SalesLineInput,
  type SalesTotals,
} from '../api/project-sales.api'
import { alsTag } from '../project-labels'
import { RegieProposalDialog } from './RegieProposalDialog'
import { ZELLE_RAHMEN, Vorschlaege, geldText, leseZahl, useListenBreite, useSuche } from './zeilenblock'
import { alsAnordnung, ausrueckenVerkauf, einrueckenVerkauf, verschiebeVerkauf } from './verkauf-anordnung'
import { alsVerkaufFilter, anzahlVerkaufFilter, filtereVerkauf, VERKAUF_FILTER_KEYS } from './verkauf-filter'
import { bestaetige, frage } from '@/lib/dialoge'

const zahl = (n: number | null | undefined, stellen = 3) =>
  n === null || n === undefined ? '—' : n.toLocaleString(navigator.language, { maximumFractionDigits: stellen })
/** Betrag im Geldformat (`geld.ts`); leer → „—". */
export const geld = (n: number | null | undefined) => geldText(n) || '—'

/** Felder wie im Belegraster (AP-32 N-88): Rand und Fläche zeigen, wo etwas einzugeben ist. */
const zelle = `${ZELLE_RAHMEN} px-1.5 py-1 text-sm`
const klein = `${ZELLE_RAHMEN} px-1.5 py-0 text-xs text-muted-foreground`
/** N-99: Luft zwischen einem Feld und dem darunter — sonst liegt der Fokusrahmen auf der Kante. */
const abstand = 'mt-1'
const knopf = 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted'

/** Ob die Filterleiste offen ist — je Browser gemerkt, eine reine Bequemlichkeit. */
const FILTER_OFFEN = 'symphenia-verkauf-filter-offen'
const FILTER_LAYOUT = 'project-sales'

/**
 * Nachtrag 15 (N15-16): Ab dieser **eigenen** Breite der Liste (nicht des Fensters) hat „Verrechnung ab" wieder
 * eine eigene Spalte. 1.460 px braucht die Tabelle mit der Spalte in voller Breite, dazu der Rahmen der Karte.
 */
const VERKAUF_BREIT_AB = 1462

export const ZUSTAND_FARBE: Record<SalesLineState, string> = {
  OPEN: 'bg-muted text-muted-foreground',
  DELIVERED: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  INVOICED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  CREDITED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  NOT_BILLABLE: 'bg-muted text-muted-foreground line-through',
}

/**
 * AP-35 J (Blatt 12 6.) — OZ der LV-Position und, wenn eine neue LV-Version sie
 * geändert hat, die Marke „LV geändert" je Feld mit **Übernehmen**. Nichts wird
 * von selbst übernommen; eine nicht mehr offene Zeile bekommt den Hinweis auf
 * eine Differenzzeile.
 */
function LvMarke({ z, onUebernehmen }: { z: SalesLine; onUebernehmen?: () => void }) {
  if (!z.lv) return null
  const offen = z.state === 'OPEN' || z.state === 'NOT_BILLABLE'
  const text = z.lv.changes
    .map((c) => `${LV_FELD[c.field] ?? c.field} ${c.field === 'unitPrice' ? geldText(c.before as number) || '—' : lvWert(c.before)} → ${c.field === 'unitPrice' ? geldText(c.after as number) || '—' : lvWert(c.after)}`)
    .join(' · ')
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
      <span className="tabular-nums">LV {z.lv.oz}</span>
      {z.lv.removed && <span className="font-medium text-destructive">im LV entfallen</span>}
      {!z.lv.removed && z.lv.changes.length > 0 && (
        <>
          <span className="font-medium text-amber-700 dark:text-amber-400">LV geändert: {text}</span>
          {offen && onUebernehmen ? (
            <button type="button" tabIndex={-1} className="rounded border border-border px-1.5 py-0.5 text-foreground hover:bg-muted" onClick={onUebernehmen}>
              Übernehmen
            </button>
          ) : (
            !offen && <span>nicht mehr offen — Differenzzeile anlegen</span>
          )}
        </>
      )}
    </div>
  )
}

export function ZustandMarke({ z }: { z: SalesLine }) {
  return (
    <span>
      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ZUSTAND_FARBE[z.state]}`}>{SALES_LINE_STATE_LABELS[z.state]}</span>
      {z.deliveryNote && <span className="block text-xs text-muted-foreground">{z.deliveryNote.number ?? 'Lieferschein'}{z.deliveredAt ? ` · ${alsTag(z.deliveredAt)}` : ''}</span>}
      {!z.deliveryNote && z.deliveredAt && <span className="block text-xs text-muted-foreground">geliefert {alsTag(z.deliveredAt)}</span>}
      {z.invoice && <span className="block text-xs text-muted-foreground">{z.invoice.number ?? 'Rechnung'}</span>}
      {z.creditNote && <span className="block text-xs text-muted-foreground">{z.creditNote.number ?? 'Gutschrift'}</span>}
      {z.inDrafts.length > 0 && <span className="block text-xs text-muted-foreground">im Entwurf {z.inDrafts.join(', ')}</span>}
    </span>
  )
}

type Einheit = { id: string; code: string }
type Artikel = { id: string; name: string; articleNumber: string; unit: { id: string; code: string } | null }

/**
 * AP-35 Detailblatt 7 2., 7. — **Reiter Verkauf an der Position.**
 *
 * Die Zeile ist der abrechenbare Anspruch, nicht die Position: Drei
 * Teillieferungen sind drei Zeilen (Teilen). Lieferschein und Rechnung
 * entstehen **nicht** hier, sondern in der Abrechnungsliste oder im
 * Arbeitsplatz — oder: markieren, kopieren, im Beleg einfügen.
 *
 * **Nachtrag 12 (20.9.2026, Vorgabe des Auftraggebers): dieselbe Position wie im
 * Belegwesen.** Gleiche Felder, gleiche Anordnung, gleiches Raster wie
 * `correspondence/components/PositionGrid` — die Zeile wandert später dorthin,
 * und zwei Anordnungen für denselben Datensatz wären zwei Masken zum Lernen. Es
 * fehlen nur **Projekt und Projektposition**: Die Zeile steht ja schon im
 * Projekt. Dazu kommen, was nur der Verkauf kennt: Zustand, „Verrechnung ab",
 * abrechenbar, interne Notiz — und die Spalte Zustand nimmt den Platz ein, den
 * am Beleg Projekt und Projektposition hatten.
 *
 * Gespeichert wird **je Zeile** über den Baustein `lib/speichern/` (AP-36 Phase
 * F): eine Stelle je Zeile (`sales:<id>`), die Zeile ist ihre Gruppe, ein
 * `<tbody>` je Zeile hält Fokus, Markierung und Griff zusammen. Das ist der
 * Unterschied zum Beleg, der seine Positionen als Ganzes speichert — dort ist er
 * begründete Ausnahme (AP-36 5.2), hier gilt die Regel.
 */
export function PositionSalesTab({ projectId, positionId, scheme }: { projectId: string; positionId: string; scheme: string | null }) {
  const { user, hasModule } = useAuthStore()
  const darf = (r: string) => erfuelltRecht(r, user?.permissions)
  const darfPflegen = darf('projects.sales.manage')
  const darfAbrechnen = darf('projects.billing.run')
  const preise = darf('projects.price.view')
  const ohneBelegwesen = !hasModule('CORRESPONDENCE')

  const [daten, setDaten] = useState<{ lines: SalesLine[]; totals: SalesTotals } | null>(null)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [regie, setRegie] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [einheiten, setEinheiten] = useState<Einheit[]>([])
  const [neuSchluessel, setNeuSchluessel] = useState(0)
  const [neuFokus, setNeuFokus] = useState(false)
  /** Zeilen, deren weitere Angaben aufgeklappt sind (wie am Beleg, N-93). */
  const [weitereOffen, setWeitereOffen] = useState<Set<string>>(new Set())
  // Auftraggeber 10.10.2026: die weiteren Felder aller Zeilen immer aufgeklappt, immer zugeklappt oder wie
  // bisher je Zeile über „weitere Felder“ (`auto`). Je Benutzer gemerkt.
  const [klappModus, setKlappModus] = useKlappModus('project_sales')
  const [filterWerte, setFilterWerte] = useState<Record<string, ActiveFilter>>({})
  const [filterOffen, setFilterOffen] = useState(() => {
    try {
      return localStorage.getItem(FILTER_OFFEN) === '1'
    } catch {
      return false
    }
  })
  const behaelter = useRef<HTMLDivElement>(null)
  // Nachtrag 15 (N15-16): Die Liste wählt ihre Anordnung nach ihrer eigenen Breite. Gemessen wird am äußeren
  // Element des Reiters — es ist so breit wie die Karte der Tabelle und hängt, anders als diese, schon beim
  // Laden im Dokument: Der Messer hängt sich nur einmal ein, beim ersten Zeichnen. Ungemessen (auch in jsdom)
  // ist die Breite 0, dann gilt die schmale Anordnung vom 9.10.2026.
  const liste = useListenBreite()
  const breit = liste.breite >= VERKAUF_BREIT_AB

  const schalteFilter = (offen: boolean) => {
    setFilterOffen(offen)
    try {
      localStorage.setItem(FILTER_OFFEN, offen ? '1' : '0')
    } catch {
      /* nur Bequemlichkeit */
    }
  }

  const laden = useCallback(async () => {
    try {
      setDaten(await fetchSalesLines(projectId, positionId))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Verkaufszeilen konnten nicht geladen werden.'))
    }
  }, [projectId, positionId])

  useEffect(() => {
    void laden()
    fetchUnits().then(setEinheiten).catch(() => undefined)
  }, [laden])

  // ── Filter ────────────────────────────────────────────────────────────────

  const filterFelder = useMemo<FilterFieldDef[]>(
    () => [
      {
        key: 'state',
        label: 'Zustand',
        type: 'select-multi',
        category: 'core',
        operators: ['is', 'is_not'],
        options: SALES_LINE_STATES.map((s) => ({ value: s, label: SALES_LINE_STATE_LABELS[s] })),
      },
      { key: 'internalNote', label: 'Interne Notiz', type: 'text', category: 'core', operators: ['contains', 'not_contains'] },
      { key: 'billableFrom', label: 'Verrechnung ab', type: 'date', category: 'core', operators: ['is', 'gt', 'lt'] },
    ],
    [],
  )

  const filter = useMemo(() => alsVerkaufFilter(filterWerte), [filterWerte])
  const filterAnzahl = anzahlVerkaufFilter(filter)
  const gefiltert = filterAnzahl > 0
  // Eigener Merkposten: `daten?.lines ?? []` wäre bei jedem Render ein neues
  // Feld und triebe jede davon abhängige Berechnung neu an.
  const alleZeilen = useMemo(() => daten?.lines ?? [], [daten])
  const zeilen = useMemo(() => filtereVerkauf(alleZeilen, filter), [alleZeilen, filter])
  const auswahl = [...markiert]

  // ── Handeln ───────────────────────────────────────────────────────────────

  async function tu(aktion: () => Promise<unknown>, erfolg?: string) {
    setFehler(null)
    setHinweis(null)
    try {
      await aktion()
      if (erfolg) setHinweis(erfolg)
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  /** Neue Reihenfolge und Ebene an den Server — `null` heißt: nichts zu tun. */
  async function ordne(neu: { id: string; parentId: string | null }[] | null) {
    if (!neu) return
    await tu(() => reorderSalesLines(projectId, positionId, neu))
  }

  async function schiebe(richtung: -1 | 1) {
    if (!auswahl.length) return
    // Der Reihe nach: jede markierte Zeile rückt um einen Platz ihrer Ebene.
    let anordnung = alsAnordnung(alleZeilen)
    const reihenfolge = richtung < 0 ? auswahl : [...auswahl].reverse()
    let baum = alleZeilen
    for (const id of reihenfolge) {
      const ebene = baum.some((z) => z.id === id) ? baum.map((z) => z.id) : (baum.find((z) => z.children.some((k) => k.id === id))?.children.map((k) => k.id) ?? [])
      const i = ebene.indexOf(id)
      const zielIndex = i + richtung
      if (i < 0 || zielIndex < 0 || zielIndex >= ebene.length) continue
      const vor = richtung < 0 ? ebene[zielIndex] : (ebene[zielIndex + 1] ?? null)
      const neu = verschiebeVerkauf(baum, id, [], vor)
      if (!neu) continue
      anordnung = neu
      baum = alsBaum(baum, neu)
    }
    await ordne(anordnung)
  }

  async function ausAblage() {
    const eintraege = (await fetchClipboard()).filter((e) => ['ARTICLE', 'TEXT', 'TITLE'].includes((e.position as { lineType?: string }).lineType ?? ''))
    if (!eintraege.length) {
      setHinweis('Die Zwischenablage hat keine Zeilen, die Verkaufszeilen werden können.')
      return
    }
    await tu(() => pasteSalesLines(projectId, positionId, eintraege.map((e) => e.id)), `${eintraege.length} Zeilen eingefügt.`)
  }

  async function teilen(z: SalesLine) {
    const eingabe = await frage(`Welche Menge bleibt an dieser Zeile? (Rest wird eine neue Zeile, gesamt ${zahl(z.quantity)} ${z.unitCode ?? ''})`)
    if (!eingabe) return
    const menge = Number(eingabe.replace(',', '.'))
    void tu(() => splitSalesLine(z.id, menge), 'Zeile geteilt.')
  }

  const schalteWeitere = (id: string) =>
    setWeitereOffen((w) => {
      const neu = new Set(w)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })

  // ── Markieren und Ziehen (wie in den anderen Listen) ──────────────────────

  const reihe = useMemo(() => zeilen.flatMap((z) => [z.id, ...z.children.map((k) => k.id)]), [zeilen])
  const { amRand } = useRandMarkieren({
    behaelter,
    reihe,
    markiert,
    setMarkiert,
    onKopieren: () => void tu(() => copySalesLines([...markiert]), 'In die Zwischenablage kopiert — im Beleg einfügen.'),
  })

  /** Die Blöcke der Ebene: eine Hauptzeile samt Unterpositionen, eine Unterposition allein unter ihren Geschwistern. */
  const bloeckeFuer = useCallback(
    (key: string) => {
      const eltern = zeilen.find((z) => z.children.some((k) => k.id === key))
      if (eltern) return eltern.children.map((k) => ({ key: k.id, zeilen: [k.id] }))
      return zeilen.map((z) => ({ key: z.id, zeilen: [z.id, ...z.children.map((k) => k.id)] }))
    },
    [zeilen],
  )

  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    bloeckeFuer,
    onAblegen: (key, vorKey) => {
      // Gefiltert heißt „ans Ende": hinter die letzte **sichtbare** Zeile der Ebene,
      // nicht ans Ende der ganzen Liste.
      let vor = vorKey
      if (vor === null && gefiltert) vor = naechsteUnsichtbare(alleZeilen, zeilen, key)
      void ordne(verschiebeVerkauf(alleZeilen, key, [...markiert], vor))
    },
  })

  const wandernd = useMemo(() => {
    if (!gezogen) return new Set<string>()
    const eltern = alleZeilen.find((z) => z.children.some((k) => k.id === gezogen))
    const ebene = eltern ? eltern.children.map((k) => k.id) : alleZeilen.map((z) => z.id)
    const ids = markiert.has(gezogen) ? ebene.filter((id) => markiert.has(id)) : [gezogen]
    const kinder = ids.flatMap((id) => alleZeilen.find((z) => z.id === id)?.children.map((k) => k.id) ?? [])
    return new Set([...ids, ...kinder])
  }, [gezogen, alleZeilen, markiert])

  // ── Tastatur (wie im Belegraster) ─────────────────────────────────────────

  const tasten = (e: KeyboardEvent<HTMLElement>) => {
    const ziel = e.target as HTMLElement
    const imFeld = !!ziel.closest('input:not([type="checkbox"]), textarea, select')
    const key = ziel.closest<HTMLElement>('[data-key]')?.dataset.key ?? null

    if (e.altKey && key && darfPflegen && ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault()
      if (e.key === 'ArrowRight') void ordne(einrueckenVerkauf(alleZeilen, key))
      if (e.key === 'ArrowLeft') void ordne(ausrueckenVerkauf(alleZeilen, key))
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        setMarkiert(new Set([key]))
        void schiebeEine(key, e.key === 'ArrowUp' ? -1 : 1)
      }
      return
    }
    if (e.key === 'Enter' && imFeld && ziel.tagName !== 'TEXTAREA' && key && !e.nativeEvent.isComposing) {
      e.preventDefault()
      naechsteZeileFokussieren(ziel, key)
      return
    }
    if (e.key === 'Escape' && imFeld && key) {
      // Das Feld hat nichts zurückzunehmen (sonst hätte der Speicherbaustein den
      // Druck schon verbraucht): Dann verlässt Esc das Feld und markiert die Zeile.
      e.preventDefault()
      ;(ziel as HTMLInputElement).blur()
      setMarkiert(new Set([key]))
      behaelter.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(key)}"]`)?.focus()
    }
  }

  async function schiebeEine(id: string, richtung: -1 | 1) {
    const ebene = alleZeilen.some((z) => z.id === id)
      ? alleZeilen.map((z) => z.id)
      : (alleZeilen.find((z) => z.children.some((k) => k.id === id))?.children.map((k) => k.id) ?? [])
    const i = ebene.indexOf(id)
    const zielIndex = i + richtung
    if (i < 0 || zielIndex < 0 || zielIndex >= ebene.length) return
    const vor = richtung < 0 ? ebene[zielIndex] : (ebene[zielIndex + 1] ?? null)
    await ordne(verschiebeVerkauf(alleZeilen, id, [], vor))
  }

  /**
   * Enter springt in **dieselbe Spalte der nächsten Zeile** (AP-32 Nachtrag 11,
   * AP-36 3.2) — gesucht über die Tabellenzelle, nicht über die laufende Nummer
   * des Feldes: Eine Hauptzeile trägt andere Felder als ihre Unterposition, die
   * Spalten stehen aber übereinander.
   */
  const naechsteZeileFokussieren = (ziel: HTMLElement, key: string) => {
    const bedienbar = (bereich: Element) =>
      [...bereich.querySelectorAll<HTMLInputElement>('input:not([type="checkbox"]), textarea, select')].filter((f) => !f.disabled && f.tabIndex >= 0 && !f.readOnly)
    const feld = ziel.closest<HTMLElement>('input:not([type="checkbox"]), textarea, select')
    const zelleDavon = feld?.closest('td')
    const zeile = zelleDavon?.closest('tr')
    const spalte = zeile && zelleDavon ? [...zeile.children].indexOf(zelleDavon) : -1
    const platz = zelleDavon && feld ? bedienbar(zelleDavon).indexOf(feld as HTMLInputElement) : -1

    const i = reihe.indexOf(key)
    const naechsterKey = i >= 0 ? reihe[i + 1] : undefined
    const naechsterKoerper = naechsterKey ? behaelter.current?.querySelector(`[data-key="${CSS.escape(naechsterKey)}"]`) : null
    const naechste = naechsterKoerper?.querySelector('tr[data-hauptzeile]') ?? null
    if (!naechste) {
      feld?.blur()
      return
    }
    // „Verrechnung ab" findet sein Gegenstück über den Namen, in beiden Anordnungen: In der zweiten Zeile steht
    // es in keiner Spalte der Hauptzeile; in der eigenen Spalte (Nachtrag 15, N15-16) steht es zwar über dem der
    // nächsten Zeile, liegt aber außerhalb der Aktivierreihenfolge — und `bedienbar` kennt nur, was in ihr liegt.
    const gleiches =
      feld?.dataset.feld && (!zeile?.hasAttribute('data-hauptzeile') || feld.tabIndex < 0)
        ? naechsterKoerper?.querySelector<HTMLElement>(`[data-feld="${feld.dataset.feld}"]:not(:disabled)`)
        : null
    const inZelle = spalte >= 0 ? bedienbar(naechste.children[spalte] ?? naechste) : []
    const zielFeld = gleiches ?? inZelle[platz] ?? inZelle[0] ?? bedienbar(naechste)[0]
    if (zielFeld) zielFeld.focus()
    else feld?.blur()
  }

  // Dasselbe äußere Element wie unten (ein `<div>` an derselben Stelle): An ihm hängt die Breitenmessung.
  if (!daten) return <div ref={liste.ref}>{fehler ? <p className="text-sm text-destructive">{fehler}</p> : <p className="text-sm text-muted-foreground">Lädt …</p>}</div>

  const t = daten.totals
  // Kästchen · Pos · Art · Menge · Einheit · Bezeichnung · Artikel · Preise (4) · [Verrechnung ab] · Zustand · Schalter
  const spaltenGesamt = 7 + (preise ? 4 : 0) + (breit ? 1 : 0) + 2
  // Die Nummer zählt über die **ganze** Liste — ein Filter versteckt Zeilen, er nummeriert sie nicht neu.
  const nummern = laufendeNummern(alleZeilen)
  const gezogeneZeile = gezogen ? [...zeilen, ...zeilen.flatMap((z) => z.children)].find((z) => z.id === gezogen) : undefined
  const alleMarkiert = reihe.length > 0 && reihe.every((id) => markiert.has(id))

  const zeile = (z: SalesLine, kind: boolean) => (
    <VerkaufZeile
      key={z.id}
      z={z}
      kind={kind}
      nummer={nummern.get(z.id) ?? ''}
      spaltenGesamt={spaltenGesamt}
      breit={breit}
      einheiten={einheiten}
      preise={preise}
      darfPflegen={darfPflegen}
      handLieferung={ohneBelegwesen}
      markiert={markiert.has(z.id)}
      onRand={amRand(z.id)}
      gezogen={wandernd.has(z.id)}
      ziellinie={ziel?.key === z.id ? ziel.kante : null}
      weitereOffen={zeileOffen(klappModus, weitereOffen.has(z.id))}
      klappModus={klappModus}
      onWeitere={() => schalteWeitere(z.id)}
      onGespeichert={laden}
      onUnterposition={kind ? undefined : () => void tu(() => createSalesLine(projectId, positionId, { name: 'Neue Unterposition', parentId: z.id }))}
      onEbeneWechseln={kind ? () => void ordne(ausrueckenVerkauf(alleZeilen, z.id)) : () => void ordne(einrueckenVerkauf(alleZeilen, z.id))}
      onSchieben={(r) => void schiebeEine(z.id, r)}
      onTeilen={() => teilen(z)}
      onLvUebernehmen={darfPflegen ? () => void tu(() => applyLvChange(z.id), 'LV-Änderung übernommen.') : undefined}
      onLoeschen={async () => {
        if (await bestaetige(z.timeEntries ? `Zeile löschen? ${z.timeEntries} Buchungen werden wieder frei.` : 'Zeile löschen?')) void tu(() => deleteSalesLine(z.id), 'Gelöscht.')
      }}
    />
  )

  return (
    <div ref={liste.ref} className="space-y-3">
      {/* Bleibt eingehängt, auch eingeklappt: Sie stellt beim Laden die zuletzt
          benutzten Filter wieder her, und der Knopf zählt sie. */}
      <div className={filterOffen ? '' : 'hidden'}>
        <FilterBar
          availableFields={filterFelder}
          value={filterWerte}
          onChange={setFilterWerte}
          layoutKey={FILTER_LAYOUT}
          defaultFieldKeys={VERKAUF_FILTER_KEYS.slice()}
          festeFelder
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-expanded={filterOffen}
          aria-label={filterAnzahl ? `Filter — ${filterAnzahl} aktiv` : 'Filter'}
          title={filterOffen ? 'Filterleiste ausblenden' : 'Filterleiste einblenden'}
          className={`${knopf} ${filterAnzahl ? 'border-primary bg-primary/10 font-medium text-primary hover:bg-primary/15' : ''} ${filterOffen && !filterAnzahl ? 'bg-muted' : ''}`}
          onClick={() => schalteFilter(!filterOffen)}
        >
          <Filter size={14} className={filterAnzahl ? 'fill-current' : ''} /> Filter
          {filterAnzahl > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] leading-4 text-primary-foreground">{filterAnzahl}</span>}
          {filterOffen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <KlappUmschalter
          modus={klappModus}
          onModus={setKlappModus}
          className="self-stretch rounded-md"
          texte={{
            gruppe: 'Weitere Felder der Zeilen',
            auf: { name: 'Weitere Felder immer aufgeklappt', titel: 'Alle Zeilen zeigen ihre weiteren Felder' },
            auto: { name: 'Weitere Felder je Zeile aufklappen', titel: 'Jede Zeile klappt einzeln über „weitere Felder“ auf' },
            zu: { name: 'Weitere Felder immer zugeklappt', titel: 'Keine Zeile zeigt ihre weiteren Felder' },
          }}
        />
        {darfPflegen && (
          <button type="button" className={knopf} onClick={() => void ausAblage()}>
            <ClipboardPaste size={14} /> Aus Ablage
          </button>
        )}
        {darfAbrechnen && (
          <button type="button" className={knopf} onClick={() => setRegie(true)}>
            <Timer size={14} /> Regie prüfen
          </button>
        )}
        {auswahl.length > 0 && (
          <div className="ml-auto flex flex-wrap items-center gap-1 text-sm" data-auswahl-behalten>
            <span className="mr-1 text-muted-foreground">{auswahl.length} markiert</span>
            <button type="button" className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => copySalesLines(auswahl), 'In die Zwischenablage kopiert — im Beleg einfügen.')}>
              <Copy size={13} /> Kopieren
            </button>
            {darfPflegen && (
              <>
                <button type="button" className="rounded border border-border p-1 hover:bg-muted" aria-label="nach oben" onClick={() => void schiebe(-1)}>
                  <ArrowUp size={14} />
                </button>
                <button type="button" className="rounded border border-border p-1 hover:bg-muted" aria-label="nach unten" onClick={() => void schiebe(1)}>
                  <ArrowDown size={14} />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}
      {gefiltert && (
        <p className="text-xs text-muted-foreground">
          {reihe.length} von {alleZeilen.length + alleZeilen.reduce((s, z) => s + z.children.length, 0)} Zeilen gefiltert
          {darfPflegen ? ' — neue Zeilen erscheinen nur, wenn sie zum Filter passen.' : '.'}{' '}
          <button type="button" className="text-primary hover:underline" onClick={() => setFilterWerte({})}>
            Filter zurücksetzen
          </button>
        </p>
      )}

      <div ref={behaelter} onKeyDown={tasten}>
        <ZiehBereich
          dndProps={dndProps}
          vorschau={
            gezogeneZeile && (
              <>
                {wandernd.size > 1 && <span className="mr-1.5 rounded bg-primary px-1.5 text-xs text-primary-foreground">{wandernd.size} Zeilen</span>}
                <span className="font-medium">{gezogeneZeile.name}</span>
              </>
            )
          }
        >
          {/* N-92: Die Liste steht auf der Kartenfläche, die Felder auf der helleren
              Hintergrundfläche — dieselbe Staffelung wie im Beleg und in der Stückliste. */}
          <div className="overflow-x-auto rounded-md border border-border bg-card">
            <datalist id="verkauf-einheiten">
              {einheiten.map((e) => (
                <option key={e.id} value={e.code} />
              ))}
            </datalist>
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
                <tr className="h-8 align-middle [&>th]:whitespace-nowrap">
                  <th className="relative w-2 p-0">
                    <div
                      data-rand
                      role="checkbox"
                      aria-checked={alleMarkiert}
                      aria-label="alle Zeilen markieren"
                      title="alle Zeilen markieren"
                      className={`absolute inset-0 ${alleMarkiert ? 'bg-primary' : 'hover:bg-primary/30'}`}
                      style={{ cursor: RAND_ZEIGER }}
                      onMouseDown={(e) => {
                        if (e.button !== 0) return
                        e.preventDefault()
                        setMarkiert(alleMarkiert ? new Set() : new Set(reihe))
                      }}
                    />
                  </th>
                  <th className="w-14 px-1.5 text-left">Pos.</th>
                  <th className="w-24 px-1.5 text-left">Art</th>
                  <th className="w-20 px-1.5 text-right">Menge</th>
                  <th className="w-16 px-1.5 text-left">Einheit</th>
                  <th className="min-w-[16rem] px-1.5 text-left">Bezeichnung</th>
                  <th className="w-28 px-1.5 text-left">Artikel-Nr.</th>
                  {preise && (
                    <>
                      <th className="w-24 px-1.5 text-right">EP</th>
                      <th className="w-20 px-1.5 text-right">PE</th>
                      <th className="w-16 px-1.5 text-right">Rabatt</th>
                      <th className="w-28 px-2 text-right">Betrag</th>
                    </>
                  )}
                  {/* „Verrechnung ab" steht je nach Breite der Liste an zwei Stellen. Schmal (Auftraggeber
                      9.10.2026): ohne eigene Spalte in der zweiten Zeile, der Platz geht an die Bezeichnung.
                      Ab 1.462 px (Nachtrag 15, N15-16): wieder als eigene Spalte neben dem Zustand — die
                      Breite, der sie weichen musste, ist wieder da. Zu sehen ist es in beiden immer, nicht
                      erst unter „weitere Felder" (Auftraggeber 20.9.2026). */}
                  {breit && <th className="w-[8.5rem] px-1.5 text-left">Verrechnung ab</th>}
                  <th className="w-40 px-1.5 text-left">Zustand</th>
                  <th className="w-24 px-1.5" />
                </tr>
              </thead>
              {zeilen.flatMap((z) => [zeile(z, false), ...z.children.map((k) => zeile(k, true))])}
              {darfPflegen && (
                <NeueVerkaufZeile
                  key={`neu-${neuSchluessel}`}
                  spaltenGesamt={spaltenGesamt}
                  breit={breit}
                  einheiten={einheiten}
                  preise={preise}
                  handLieferung={ohneBelegwesen}
                  autoFokus={neuFokus}
                  weitereOffen={zeileOffen(klappModus, weitereOffen.has('neu'))}
                  klappModus={klappModus}
                  onWeitere={() => schalteWeitere('neu')}
                  onAnlegen={async (dto, perTastatur) => {
                    await createSalesLine(projectId, positionId, dto as SalesLineInput & { name: string })
                    await laden()
                    setNeuFokus(perTastatur)
                    setNeuSchluessel((k) => k + 1)
                  }}
                />
              )}
              {zeilen.length === 0 && !darfPflegen && (
                <tbody>
                  <tr>
                    <td colSpan={spaltenGesamt} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      {gefiltert ? 'Keine Zeile passt zum Filter.' : 'Noch keine Verkaufszeilen.'}
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
            <div className="flex flex-wrap justify-between gap-2 border-t-2 border-border bg-muted/40 px-3 py-1.5 text-xs font-medium">
              <span>
                {t.lines} abrechenbare Zeilen · {t.open} offen · {t.delivered} geliefert · {t.invoiced} abgerechnet
              </span>
              {preise && (
                <span className="text-right tabular-nums">
                  {geld(t.value)}
                  <span className="block font-normal text-muted-foreground">
                    offen {geld(t.openValue)} · geliefert {geld(t.deliveredValue)}
                  </span>
                </span>
              )}
            </div>
          </div>
        </ZiehBereich>
      </div>

      {regie && (
        <RegieProposalDialog
          positionId={positionId}
          scheme={scheme}
          onClose={() => setRegie(false)}
          onDone={async () => {
            setRegie(false)
            setHinweis('Regievorschlag übernommen.')
            await laden()
          }}
        />
      )}
    </div>
  )
}

// ── Rechnen an der Liste ──────────────────────────────────────────────────────

/** Laufende Nummer je Zeile: nur Zeilen mit Wert zählen, Unterpositionen als `3.1`. */
function laufendeNummern(zeilen: readonly SalesLine[]): Map<string, string> {
  const aus = new Map<string, string>()
  let n = 0
  for (const z of zeilen) {
    if (z.lineType !== 'ITEM') continue
    n++
    aus.set(z.id, String(n))
    let m = 0
    for (const k of z.children) {
      if (k.lineType !== 'ITEM') continue
      m++
      aus.set(k.id, `${n}.${m}`)
    }
  }
  return aus
}

/** Die Anordnung wieder als Baum — für mehrere Schritte hintereinander. */
function alsBaum(baum: readonly SalesLine[], anordnung: readonly { id: string; parentId: string | null }[]): SalesLine[] {
  const alle = new Map([...baum, ...baum.flatMap((z) => z.children)].map((z) => [z.id, z]))
  return anordnung
    .filter((a) => a.parentId === null)
    .map((a) => ({ ...alle.get(a.id)!, children: anordnung.filter((k) => k.parentId === a.id).map((k) => alle.get(k.id)!) }))
}

/**
 * Gefiltert heißt „ans Ende": Gesucht ist die erste **unsichtbare** Zeile hinter
 * der letzten sichtbaren derselben Ebene — vor ihr wird abgelegt. Gibt es keine,
 * geht es wirklich ans Ende (`null`).
 */
function naechsteUnsichtbare(alle: readonly SalesLine[], sichtbar: readonly SalesLine[], gezogen: string): string | null {
  const eltern = alle.find((z) => z.children.some((k) => k.id === gezogen))
  const ebene = eltern ? eltern.children.map((k) => k.id) : alle.map((z) => z.id)
  const sichtbareEbene = eltern
    ? (sichtbar.find((z) => z.id === eltern.id)?.children.map((k) => k.id) ?? [])
    : sichtbar.map((z) => z.id)
  const letzte = sichtbareEbene[sichtbareEbene.length - 1]
  if (!letzte) return null
  return ebene[ebene.indexOf(letzte) + 1] ?? null
}

// ── Zeile ─────────────────────────────────────────────────────────────────────

/**
 * Props eines Eingabefelds der Zeile. Bestehende Zeilen liefern sie aus dem
 * Speicherbaustein (`useFeld(...).props`), die leere letzte Zeile aus ihrem
 * lokalen Entwurf — die Darstellung darunter ist dieselbe.
 */
type Eingabe = {
  value: string
  disabled?: boolean
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  name?: string
  'aria-invalid'?: boolean
  onBlur?: () => void
  onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void
  ref?: (el: HTMLElement | null) => void
}

type Schalter = {
  checked: boolean
  disabled?: boolean
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  name?: string
  ref?: (el: HTMLElement | null) => void
}

interface ZeilenFelder {
  lineType: Eingabe
  quantity: Eingabe
  altQuantity: Eingabe
  unitId: Eingabe
  altUnitCode: Eingabe
  name: Eingabe
  longText: Eingabe
  introText: Eingabe
  customerReference: Eingabe
  unitPrice: Eingabe
  priceUnit: Eingabe
  discountPercent: Eingabe
  internalNote: Eingabe
  billableFrom: Eingabe
  deliveredAt: Eingabe
  isBillable: Schalter
  subMode?: Eingabe
}

interface Entwurf {
  lineType: SalesLineType
  name: string
  articleId: string | null
  articleNumber: string | null
  longText: string
  introText: string
  customerReference: string
  quantity: string
  altQuantity: string
  unitId: string
  altUnitCode: string
  unitPrice: string
  priceUnit: string
  discountPercent: string
  isBillable: boolean
  billableFrom: string
  internalNote: string
  deliveredAt: string
}

function leererEntwurf(): Entwurf {
  return {
    lineType: 'ITEM',
    name: '',
    articleId: null,
    articleNumber: null,
    longText: '',
    introText: '',
    customerReference: '',
    quantity: '',
    altQuantity: '',
    unitId: '',
    altUnitCode: '',
    unitPrice: '',
    priceUnit: '1',
    discountPercent: '',
    isBillable: true,
    billableFrom: '',
    internalNote: '',
    deliveredAt: '',
  }
}

/** Ein Wechsel nach `gespeichert` heißt: Der Server hat neue abgeleitete Werte (Zustand, GP, Summen). */
function useNachSpeichern(zustand: Zustand, onGespeichert: () => void) {
  const vorher = useRef<Zustand>(zustand)
  useEffect(() => {
    if (zustand === 'gespeichert' && vorher.current !== 'gespeichert') onGespeichert()
    vorher.current = zustand
  }, [zustand, onGespeichert])
}

interface ZeilenProps {
  kind: boolean
  nummer: string
  spaltenGesamt: number
  /** Nachtrag 15 (N15-16): Die Liste ist breit genug — „Verrechnung ab" steht in eigener Spalte statt in der zweiten Zeile. */
  breit: boolean
  einheiten: Einheit[]
  preise: boolean
  darfPflegen: boolean
  handLieferung: boolean
  weitereOffen: boolean
  /** Vorgabe der Liste: Bei „immer auf“ und „immer zu“ entfällt der Schalter je Zeile. */
  klappModus: KlappModus
  onWeitere: () => void
  markiert?: boolean
  onRand?: (e: ReactMouseEvent) => void
  gezogen?: boolean
  ziellinie?: 'oben' | 'unten' | null
  onUnterposition?: () => void
  onEbeneWechseln?: () => void
  onSchieben?: (richtung: -1 | 1) => void
  onTeilen?: () => void
  onLoeschen?: () => void
  /** AP-35 J: „LV geändert" übernehmen. */
  onLvUebernehmen?: () => void
}

/** Bestehende Zeile: eine Speicherstelle, die Zeile ist ihre Gruppe (AP-36). */
function VerkaufZeile({ z, onGespeichert, ...rest }: ZeilenProps & { z: SalesLine; onGespeichert: () => void }) {
  // Blatt 7 7.: offen → alles; geliefert → nur Preis und Text; abgerechnet → nur die Notiz.
  const zustandOffen = z.state === 'OPEN' || z.state === 'NOT_BILLABLE'
  const zu = !zustandOffen && z.state !== 'DELIVERED'
  const sperreText = !rest.darfPflegen || zu
  const sperreMenge = !rest.darfPflegen || !zustandOffen

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `sales:${z.id}`,
    bezeichnung: z.name,
    daten: z as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(`/projects/sales-lines/${z.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/projects/sales-lines/${z.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: z.name })

  const art = useFeld<SalesLineType>(stelle, 'lineType', { gruppe, art: 'auswahl', gesperrt: sperreMenge || rest.kind || z.timeEntries > 0 || z.children.length > 0, label: 'Art' })
  const menge = useFeld<number | null>(stelle, 'quantity', { gruppe, art: 'zahl', pflicht: true, gesperrt: sperreMenge, label: 'Menge' })
  const altMenge = useFeld<number | null>(stelle, 'altQuantity', { gruppe, art: 'zahl', gesperrt: sperreMenge, label: 'Alternativmenge' })
  const einheit = useFeld(stelle, 'unitId', { gruppe, art: 'auswahl', gesperrt: sperreMenge, label: 'Einheit' })
  const altEinheit = useFeld<string | null>(stelle, 'altUnitCode', { gruppe, gesperrt: sperreMenge, label: 'Alternativeinheit', lies: (t) => t.trim() || null })
  const name = useFeld<string | null>(stelle, 'name', { gruppe, pflicht: true, gesperrt: sperreText, label: 'Bezeichnung', lies: (t) => t.trim() || null })
  const artikel = useFeld<string | null>(stelle, 'articleId', { gruppe, gesperrt: sperreText, label: 'Artikel' })
  const langtext = useFeld<string | null>(stelle, 'longText', { gruppe, gesperrt: sperreText, label: 'Langtext', lies: (t) => t.trim() || null })
  const einleitung = useFeld<string | null>(stelle, 'introText', { gruppe, gesperrt: sperreText, label: 'Positionsbeschreibung', lies: (t) => t.trim() || null })
  const auftragsnummer = useFeld<string | null>(stelle, 'customerReference', { gruppe, gesperrt: sperreText, label: 'Auftragsnummer', lies: (t) => t.trim() || null })
  const preis = useFeld<number | null>(stelle, 'unitPrice', { gruppe, art: 'zahl', stellen: 2, gesperrt: sperreText, label: 'EP' })
  const preisEinheit = useFeld<number>(stelle, 'priceUnit', { gruppe, art: 'zahl', gesperrt: sperreText, label: 'Preiseinheit', lies: (t) => leseZahl(t) ?? 1 })
  const rabatt = useFeld<number | null>(stelle, 'discountPercent', { gruppe, art: 'zahl', stellen: 2, gesperrt: sperreText, label: 'Rabatt %' })
  const abrechenbar = useFeld<boolean>(stelle, 'isBillable', { gruppe, art: 'schalter', gesperrt: sperreMenge, label: 'abrechenbar' })
  const verrechnungAb = useFeld<string | null>(stelle, 'billableFrom', { gruppe, art: 'datum', gesperrt: !rest.darfPflegen || zu, label: 'Verrechnung ab' })
  const geliefert = useFeld<string | null>(stelle, 'deliveredAt', { gruppe, art: 'datum', gesperrt: sperreMenge, label: 'ausgeliefert am' })
  const notiz = useFeld<string | null>(stelle, 'internalNote', { gruppe, gesperrt: !rest.darfPflegen, label: 'Interne Notiz', lies: (t) => t.trim() || null })
  const unterModus = useFeld(stelle, 'subMode', { gruppe, art: 'auswahl', gesperrt: sperreMenge, label: 'Unterpositionen' })

  useNachSpeichern(gruppe.zustand, onGespeichert)

  // Der GP rechnet mit, sobald Menge, EP, Preiseinheit oder Rabatt getippt sind — nicht erst, wenn die Zeile
  // verlassen und gespeichert ist (Auftraggeber 7.10.2026); mit der Formel des Servers. Eine Zeile, die ihre
  // Unterpositionen summiert, trägt keinen eigenen Preis: Ihr GP folgt den Unterpositionen beim Speichern.
  const summiert = z.subMode === 'SUM_OF_CHILDREN' && z.children.length > 0
  const gesamt =
    !summiert && eingabeOffen(menge, preis, preisEinheit, rabatt)
      ? verkaufZeilenSumme({ quantity: menge.wert ?? 0, unitPrice: preis.wert ?? null, priceUnit: preisEinheit.wert, discountPercent: rabatt.wert ?? null })
      : z.lineTotal

  /** Der eben gewählte Artikel trägt seine Nummer sofort, noch vor der Antwort des Servers. */
  const [gewaehlt, setGewaehlt] = useState<Artikel | null>(null)
  const artikelNummer = gewaehlt && gewaehlt.id === artikel.wert ? gewaehlt.articleNumber : z.articleNumber

  return (
    <VerkaufZeileAnsicht
      {...rest}
      z={z}
      gesamt={gesamt}
      gruppe={gruppe}
      artikelId={artikel.wert}
      artikelNummer={artikelNummer}
      onArtikelWahl={(a) => {
        setGewaehlt(a)
        name.tippe(a.name)
        artikel.tippe(a.id)
        // Die Einheit ist eine Auswahl und speichert sofort — sie nimmt Bezeichnung und Artikel mit.
        if (a.unit) einheit.tippe(a.unit.id)
      }}
      onVerwerfen={() => setGewaehlt(null)}
      felder={{
        lineType: art.props,
        quantity: menge.props,
        altQuantity: altMenge.props,
        unitId: einheit.props,
        altUnitCode: altEinheit.props,
        name: name.props,
        longText: langtext.props,
        introText: einleitung.props,
        customerReference: auftragsnummer.props,
        unitPrice: preis.props,
        priceUnit: preisEinheit.props,
        discountPercent: rabatt.props,
        internalNote: notiz.props,
        billableFrom: verrechnungAb.props,
        deliveredAt: geliefert.props,
        isBillable: abrechenbar.schalter,
        subMode: unterModus.props,
      }}
    />
  )
}

/** Die leere letzte Zeile: legt beim Verlassen an (AP-36 Fall 2 „Anlegen"). */
function NeueVerkaufZeile({
  onAnlegen,
  autoFokus,
  ...rest
}: Omit<ZeilenProps, 'kind' | 'nummer' | 'markiert' | 'onRand' | 'darfPflegen'> & {
  autoFokus?: boolean
  onAnlegen: (dto: SalesLineInput, perTastatur: boolean) => Promise<void>
}) {
  const [w, setW] = useState<Entwurf>(leererEntwurf)
  const [fehler, setFehler] = useState<string | null>(null)
  const setze = (teil: Partial<Entwurf>) => setW((x) => ({ ...x, ...teil }))
  const feld = (k: keyof Omit<Entwurf, 'isBillable' | 'articleId' | 'articleNumber' | 'lineType'>): Eingabe => ({
    value: w[k],
    onChange: (e) => setze({ [k]: e.target.value } as Partial<Entwurf>),
  })

  /** Nur, was der Benutzer wirklich gefüllt hat — ein leeres Feld überschreibt die Vorgabe des Servers nicht. */
  function angaben(): SalesLineInput {
    const alt = leererEntwurf()
    const dto: SalesLineInput = {}
    const text = (k: 'internalNote' | 'longText' | 'introText' | 'customerReference' | 'altUnitCode') => {
      if (w[k] !== alt[k]) dto[k] = w[k].trim() || null
    }
    text('internalNote')
    text('longText')
    text('introText')
    text('customerReference')
    text('altUnitCode')
    if (w.articleId !== alt.articleId) dto.articleId = w.articleId
    if (w.altQuantity !== alt.altQuantity) dto.altQuantity = leseZahl(w.altQuantity)
    if (w.billableFrom !== alt.billableFrom) dto.billableFrom = w.billableFrom || null
    if (rest.preise) {
      if (w.unitPrice !== alt.unitPrice) dto.unitPrice = leseZahl(w.unitPrice)
      if (w.priceUnit !== alt.priceUnit) dto.priceUnit = leseZahl(w.priceUnit) ?? 1
      if (w.discountPercent !== alt.discountPercent) dto.discountPercent = leseZahl(w.discountPercent)
    }
    if (w.unitId !== alt.unitId) dto.unitId = w.unitId || null
    if (w.isBillable !== alt.isBillable) dto.isBillable = w.isBillable
    if (rest.handLieferung && w.deliveredAt !== alt.deliveredAt) dto.deliveredAt = w.deliveredAt || null
    return dto
  }

  async function anlegen(perTastatur: boolean) {
    if (!w.name.trim()) return
    const dto = angaben()
    dto.name = w.name.trim()
    dto.lineType = w.lineType
    dto.quantity = w.lineType === 'ITEM' ? (leseZahl(w.quantity) ?? 1) : 0
    // Artikel ohne eigenen Preis: der Server setzt den Kundenpreis ein.
    if (dto.unitPrice === null) delete dto.unitPrice
    try {
      setFehler(null)
      await onAnlegen(dto, perTastatur)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Zeile ließ sich nicht speichern.'))
    }
  }

  return (
    <VerkaufZeileAnsicht
      {...rest}
      kind={false}
      nummer=""
      darfPflegen
      autoFokus={autoFokus}
      fehler={fehler}
      // Auch die neue Zeile zeigt ihren GP, sobald ein Preis dasteht — eine leere Menge zählt 1, wie beim Anlegen.
      gesamt={
        w.lineType === 'ITEM' && w.unitPrice.trim()
          ? verkaufZeilenSumme({ quantity: leseZahl(w.quantity) ?? 1, unitPrice: leseZahl(w.unitPrice), priceUnit: leseZahl(w.priceUnit) ?? 1, discountPercent: leseZahl(w.discountPercent) })
          : null
      }
      onVerlassen={(t) => void anlegen(t)}
      onVerwerfen={() => setW(leererEntwurf())}
      artikelId={w.articleId}
      artikelNummer={w.articleNumber}
      onArtikelWahl={(a) => setze({ name: a.name, articleId: a.id, articleNumber: a.articleNumber, ...(a.unit ? { unitId: a.unit.id } : {}) })}
      felder={{
        lineType: { value: w.lineType, onChange: (e) => setze({ lineType: e.target.value as SalesLineType }) },
        quantity: feld('quantity'),
        altQuantity: feld('altQuantity'),
        unitId: feld('unitId'),
        altUnitCode: feld('altUnitCode'),
        name: feld('name'),
        longText: feld('longText'),
        introText: feld('introText'),
        customerReference: feld('customerReference'),
        unitPrice: feld('unitPrice'),
        priceUnit: feld('priceUnit'),
        discountPercent: feld('discountPercent'),
        internalNote: feld('internalNote'),
        billableFrom: feld('billableFrom'),
        deliveredAt: feld('deliveredAt'),
        isBillable: { checked: w.isBillable, onChange: (e) => setze({ isBillable: e.target.checked }) },
      }}
    />
  )
}

/** Eine Beschriftung über einem Zusatzfeld — sie steht im Raster, nicht im Kopf. */
const zusatzLabel = (text: string) => <span className="block truncate text-[11px] text-muted-foreground">{text}</span>

/**
 * Die Darstellung — gleich für die bestehende und die leere Zeile. Ein `<tbody>`
 * je Zeile: Es hält die bis zu drei `<tr>` zusammen, trägt `data-key` für Griff
 * und Markierung und ist zugleich der Bereich, dessen Verlassen speichert.
 *
 * Zwei Anordnungen, gewählt von der Liste nach ihrer Breite (`breit`, Nachtrag 15
 * N15-16): schmal steht „Verrechnung ab" in der zweiten Zeile (Nachtrag 14), breit
 * in einer eigenen Spalte der Hauptzeile. Alles andere ist in beiden gleich.
 */
function VerkaufZeileAnsicht({
  z,
  kind,
  nummer,
  spaltenGesamt,
  breit,
  einheiten,
  preise,
  darfPflegen,
  handLieferung,
  weitereOffen,
  klappModus,
  onWeitere,
  felder,
  gesamt,
  artikelId,
  artikelNummer,
  onArtikelWahl,
  gruppe,
  onVerlassen,
  onVerwerfen,
  fehler,
  autoFokus,
  markiert,
  onRand,
  gezogen,
  ziellinie,
  onUnterposition,
  onEbeneWechseln,
  onSchieben,
  onTeilen,
  onLoeschen,
  onLvUebernehmen,
}: ZeilenProps & {
  z?: SalesLine
  darfPflegen: boolean
  felder: ZeilenFelder
  /** GP der Zeile: der gespeicherte oder — solange getippt wird — der aus den Eingaben gerechnete. */
  gesamt?: number | null
  artikelId: string | null
  artikelNummer: string | null
  onArtikelWahl: (a: Artikel) => void
  gruppe?: Gruppe
  onVerlassen?: (perTastatur: boolean) => void
  onVerwerfen?: () => void
  fehler?: string | null
  autoFokus?: boolean
}) {
  const art = (z ? z.lineType : (felder.lineType.value as SalesLineType)) ?? 'ITEM'
  const mitWerten = art === 'ITEM'
  const [suchen, setSuchen] = useState(false)
  const [treffer, setTreffer] = useSuche<Artikel>(felder.name.value, suchen && mitWerten, searchArticles)
  /** Suche über die Artikelnummer — getippt wird hier, gespeichert wird der Artikel. */
  const [nrText, setNrText] = useState<string | null>(null)
  const [trefferNr, setTrefferNr] = useSuche<Artikel>(nrText ?? '', nrText !== null && mitWerten, searchArticles)
  const koerper = useRef<HTMLTableSectionElement | null>(null)
  const greifen = useZeileGreifen(z?.id, !darfPflegen || !z)
  const perTastatur = useRef(false)
  /** In der leeren Zeile wurde etwas eingegeben — sie hat beim Verlassen etwas anzulegen. */
  const beruehrt = useRef(false)
  const sucheBeenden = () => {
    setSuchen(false)
    setTreffer([])
  }

  useEffect(() => {
    if (autoFokus) koerper.current?.querySelector<HTMLElement>('[data-start]')?.focus()
  }, [autoFokus])

  const zustandOffen = !z || z.state === 'OPEN' || z.state === 'NOT_BILLABLE'
  const zu = Boolean(z) && !zustandOffen && z?.state !== 'DELIVERED'
  const summeDerKinder = Boolean(z && z.subMode === 'SUM_OF_CHILDREN' && z.children.length)
  const preisFeld = mitWerten && !summeDerKinder
  const grau = kind ? 'text-muted-foreground' : ''
  const toenung = markiert ? 'bg-primary/15' : kind ? 'bg-muted/10' : ''
  const abgeblendet = gezogen ? 'opacity-40' : z?.state === 'NOT_BILLABLE' ? 'opacity-60' : ''
  const zusatzOffen = mitWerten && weitereOffen
  // Schmal hat eine Zeile mit Werten immer eine zweite Zeile (ab der Artikelspalte, wie am Beleg — N-97): Dort
  // steht „Verrechnung ab", aufgeklappt daneben die weiteren Felder. Breit (Nachtrag 15, N15-16) steht
  // „Verrechnung ab" in seiner Spalte der Hauptzeile; die zweite Zeile gibt es dann nur aufgeklappt — zugeklappt
  // hätte sie nichts zu zeigen, und eine leere Zeile trüge trotzdem Trenn- und Ziellinie an der falschen Kante.
  const zweiteZeile = mitWerten && (!breit || zusatzOffen)
  // Die linken Zellen reichen über beide Zeilen.
  const zusatzZeilen = zweiteZeile ? 2 : undefined
  // N-67: die Ziellinie als Innenschatten an den Zellen — ein Schatten am <tr> zeichnet nicht jeder Browser.
  // Die untere Linie gehört an die Unterkante des Blocks: mit zweiter Zeile an diese und an die Zellen der
  // Hauptzeile, die über beide reichen — nicht an die, die in der Mitte enden. Ohne zweite Zeile enden alle
  // Zellen der Hauptzeile an der Unterkante.
  const linieUnten = '[&>td]:shadow-[inset_0_-2px_0_hsl(var(--primary))]'
  const linie =
    ziellinie === 'oben'
      ? '[&>td]:shadow-[inset_0_2px_0_hsl(var(--primary))]'
      : ziellinie === 'unten'
        ? zweiteZeile
          ? '[&>td[rowspan]]:shadow-[inset_0_-2px_0_hsl(var(--primary))]'
          : linieUnten
        : ''
  const hatWeitere = Boolean(z && (z.introText || z.customerReference || z.internalNote))

  return (
    <tbody
      ref={(el) => {
        koerper.current = el
        greifen.ref(el)
      }}
      {...greifen.listeners}
      data-key={z?.id}
      tabIndex={z ? -1 : undefined}
      className={`${greifen.listeners ? 'zeile-greifbar' : ''} ${!z ? 'bg-muted/20' : ''}`}
      onFocus={gruppe?.onFocus}
      onBlur={(e) => {
        gruppe?.onBlur(e)
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
        sucheBeenden()
        const tastatur = perTastatur.current
        perTastatur.current = false
        onVerlassen?.(tastatur)
      }}
      onChange={
        z
          ? undefined
          : () => {
              beruehrt.current = true
            }
      }
      onKeyDown={(e) => {
        if (e.key === 'Tab' || e.key === 'Enter') perTastatur.current = true
        // KA-53: Im letzten Feld der leeren Zeile verlässt Tab sie wie im Zeilenblock — das Feld gibt den Fokus
        // ab, die Zeile wird angelegt, die nächste leere bekommt ihn. Dem Browser überlassen, ginge er zuerst
        // zum nächsten Bedienelement der Seite oder aus ihr hinaus; von dort kommt er nicht zurück.
        if (e.key === 'Tab' && !e.shiftKey && !z && beruehrt.current && koerper.current) {
          const felder = [...koerper.current.querySelectorAll<HTMLInputElement>('input, textarea, select')].filter((f) => !f.disabled && f.tabIndex >= 0)
          if (felder[felder.length - 1] === e.target) {
            e.preventDefault()
            ;(e.target as HTMLElement).blur()
          }
        }
        if (e.key === 'Escape' && onVerwerfen) {
          beruehrt.current = false
          onVerwerfen()
        }
      }}
    >
      {zusatzOffen && (
        // Wie am Beleg (N-95): Auftragsnummer über Menge und Einheit,
        // Positionsbeschreibung über Bezeichnung und Artikelnummer.
        <tr className={`align-bottom ${toenung} ${abgeblendet}`}>
          <td colSpan={3} />
          <td colSpan={2} className="px-1.5 pt-1">
            <label className="block" title="Leer heißt: wie im Beleg.">
              {zusatzLabel('Auftragsnummer')}
              <input tabIndex={-1} className={`${zelle} text-xs`} maxLength={100} {...felder.customerReference} />
            </label>
          </td>
          <td colSpan={2} className="px-1.5 pt-1">
            <label className="block">
              {zusatzLabel('Positionsbeschreibung')}
              <textarea tabIndex={-1} className={`${zelle} block h-[calc(1.5rem+2px)] resize-y text-xs leading-4`} {...felder.introText} />
            </label>
          </td>
          <td colSpan={spaltenGesamt - 7} />
        </tr>
      )}

      <tr
        data-hauptzeile
        className={`${zweiteZeile ? '' : 'border-b border-border'} align-top focus:outline-none ${toenung} ${abgeblendet} ${linie}`}
      >
        <td className="relative w-2 p-0" rowSpan={zusatzZeilen}>
          {z && onRand ? (
            <div
              data-rand
              role="checkbox"
              aria-checked={Boolean(markiert)}
              aria-label={`${z.name} markieren`}
              title="Zeile markieren — Strg: dazu, Umschalt: Bereich"
              className={`absolute inset-0 ${markiert ? 'bg-primary' : 'hover:bg-primary/30'}`}
              style={{ cursor: RAND_ZEIGER }}
              onMouseDown={onRand}
            />
          ) : null}
        </td>
        <td className="px-1.5 py-1 font-mono text-xs text-muted-foreground" rowSpan={zusatzZeilen}>
          {nummer || '·'}
        </td>
        <td className="px-1.5 py-1" rowSpan={zusatzZeilen}>
          <select className={zelle} aria-label="Zeilenart" {...felder.lineType}>
            {SALES_LINE_TYPES.map((a) => (
              <option key={a} value={a}>
                {SALES_LINE_TYPE_LABELS[a]}
              </option>
            ))}
          </select>
        </td>
        <td className="px-1.5 py-1" rowSpan={zusatzZeilen}>
          {mitWerten && (
            <>
              <input data-feld="quantity" className={`${zelle} text-right tabular-nums ${grau}`} inputMode="decimal" placeholder={z ? '' : '1'} {...felder.quantity} />
              {/* Alternativmenge — Angabe, kein Rechenweg (wie am Beleg). */}
              <input
                tabIndex={-1}
                className={`${klein} ${abstand} text-right tabular-nums`}
                inputMode="decimal"
                placeholder="Alt."
                title="Menge in der Alternativeinheit"
                {...felder.altQuantity}
              />
            </>
          )}
        </td>
        <td className="px-1.5 py-1" rowSpan={zusatzZeilen}>
          {mitWerten && (
            <>
              <select className={`${zelle} ${grau}`} {...felder.unitId}>
                <option value="">{z ? '—' : 'wie Position'}</option>
                {einheiten.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code}
                  </option>
                ))}
              </select>
              <input
                tabIndex={-1}
                list="verkauf-einheiten"
                className={`${klein} ${abstand}`}
                placeholder="Alt."
                title="Alternativeinheit"
                maxLength={20}
                {...felder.altUnitCode}
              />
            </>
          )}
        </td>
        <td className="px-1.5 py-1" style={{ paddingLeft: kind ? '1.5rem' : undefined }} rowSpan={zusatzZeilen}>
          <div className="relative">
            <input
              data-start
              className={`${zelle} ${!kind && art !== 'TEXT' ? 'font-semibold' : ''} ${art === 'TEXT' ? 'italic' : ''} ${grau}`}
              placeholder={z ? '' : art === 'TEXT' ? 'neue Textzeile' : art === 'TITLE' ? 'neue Überschrift' : 'neue Zeile — Artikel suchen oder Bezeichnung eingeben'}
              {...felder.name}
              onChange={(e) => {
                felder.name.onChange(e)
                setSuchen(true)
              }}
            />
            <Vorschlaege
              allein
              eintraege={treffer}
              schluessel={(a) => a.id}
              onWahl={(a) => {
                onArtikelWahl(a)
                sucheBeenden()
              }}
              darstellung={(a) => (
                <>
                  {a.name} <span className="font-mono text-[11px] text-muted-foreground">{a.articleNumber}</span>
                </>
              )}
            />
          </div>
          {z && <LvMarke z={z} onUebernehmen={onLvUebernehmen} />}
          {art !== 'TITLE' && (
            <textarea
              className={`${zelle} ${abstand} block h-[calc(2.5rem+6px)] resize-y text-xs text-muted-foreground`}
              placeholder="Langtext"
              {...felder.longText}
            />
          )}
          {mitWerten && (
            <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {darfPflegen && !kind && z && onUnterposition && (
                <button type="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={onUnterposition} className="inline-flex items-center gap-0.5 text-primary hover:underline" title="Eine Unterposition an dieser Position anlegen">
                  <Plus className="h-3 w-3" />
                  Unterposition
                </button>
              )}
              <label className="flex items-center gap-1">
                <input type="checkbox" tabIndex={-1} {...felder.isBillable} />
                abrechenbar
              </label>
              {z && z.children.length > 0 && felder.subMode && (
                <select tabIndex={-1} className="rounded border border-input bg-background px-1 text-xs" aria-label="Unterpositionen" {...felder.subMode}>
                  <option value="SUM_OF_CHILDREN">Summe der Unterpositionen</option>
                  <option value="OWN_PRICE">eigener Preis</option>
                </select>
              )}
              {klappModus === 'auto' ? (
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onWeitere}
                  aria-expanded={weitereOffen}
                  className={`inline-flex items-center gap-0.5 hover:text-foreground ${hatWeitere ? 'text-foreground' : ''}`}
                  title="Auftragsnummer, Positionsbeschreibung, ausgeliefert am, interne Notiz"
                >
                  {weitereOffen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  weitere Felder
                  {hatWeitere && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />}
                </button>
              ) : (
                klappModus === 'zu' &&
                hatWeitere && (
                  <span className="inline-flex items-center gap-1 text-foreground" title="Diese Zeile trägt weitere Felder — verborgen, solange „immer zugeklappt“ gewählt ist">
                    weitere Felder
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                  </span>
                )
              )}
            </div>
          )}
          {fehler && <p className="mt-1 text-xs text-destructive">{fehler}</p>}
          {z && zu && !kind && <p className="mt-1 text-xs text-muted-foreground">Abgerechnet — Korrektur über eine Gutschrift im Belegwesen. Änderbar bleibt nur die interne Notiz.</p>}
          {z && z.state === 'DELIVERED' && !kind && <p className="mt-1 text-xs text-muted-foreground">Geliefert — änderbar sind nur noch Preis und Text.</p>}
        </td>
        <td className="relative px-1.5 py-1">
          {/* Wie am Beleg: In dieser Spalte lässt sich der Artikel auch über seine
              Nummer suchen. Gespeichert wird der Artikel, nicht der getippte Text —
              die Nummer gehört dem Artikelstamm (AP-28). */}
          {mitWerten && (
            <>
              <input
                className={`${zelle} font-mono ${grau}`}
                placeholder="Nr. suchen"
                disabled={felder.name.disabled}
                value={nrText ?? artikelNummer ?? ''}
                onChange={(e) => setNrText(e.target.value)}
                onBlur={() => {
                  setNrText(null)
                  setTrefferNr([])
                }}
              />
              <Vorschlaege
                allein
                eintraege={trefferNr}
                schluessel={(a) => a.id}
                onWahl={(a) => {
                  onArtikelWahl(a)
                  setNrText(null)
                  setTrefferNr([])
                }}
                darstellung={(a) => (
                  <>
                    <span className="font-mono text-[11px]">{a.articleNumber}</span> {a.name}
                  </>
                )}
              />
            </>
          )}
        </td>
        {preise && (
          <>
            <td className="px-1.5 py-1">
              {preisFeld && (
                <GeldFeld
                  className={`${zelle} ${grau}`}
                  wert={felder.unitPrice.value}
                  onWert={(t) => geldGetippt(felder.unitPrice, t)}
                  disabled={felder.unitPrice.disabled}
                  name={felder.unitPrice.name}
                  onBlur={felder.unitPrice.onBlur}
                  onKeyDown={felder.unitPrice.onKeyDown}
                  ref={felder.unitPrice.ref as ((el: HTMLInputElement | null) => void) | undefined}
                  // Artikel gewählt, aber noch kein Preis getippt: Der Server setzt den Kundenpreis ein.
                  placeholder={artikelId && !z ? 'Kundenpreis' : ''}
                />
              )}
            </td>
            <td className="px-1.5 py-1">{preisFeld && <input className={`${zelle} text-right tabular-nums ${grau}`} inputMode="numeric" title="Preiseinheit" {...felder.priceUnit} />}</td>
            <td className="px-1.5 py-1">{preisFeld && <input className={`${zelle} text-right tabular-nums ${grau}`} inputMode="decimal" {...felder.discountPercent} />}</td>
            <td className={`px-2 py-1.5 text-right tabular-nums ${kind ? 'text-muted-foreground' : 'font-semibold'}`}>{mitWerten ? geldText(gesamt) : ''}</td>
          </>
        )}
        {breit && (
          // Nachtrag 15 (N15-16): „Verrechnung ab" in eigener Spalte, ohne Beschriftung — die steht im Kopf.
          // Es bleibt außerhalb der Aktivierreihenfolge wie in der zweiten Zeile (N14-2): Tab führt durch die
          // Zeile, nicht durch den Ausnahmefall. Enter findet das Feld der nächsten Zeile über `data-feld`.
          <td className="px-1.5 py-1">
            {mitWerten && (
              <input
                type="date"
                tabIndex={-1}
                data-feld="billableFrom"
                aria-label="Verrechnung ab"
                title="Ab wann die Zeile verrechnet werden darf. Leer heißt: sofort möglich."
                className={`${zelle} ${grau}`}
                {...felder.billableFrom}
              />
            )}
          </td>
        )}
        <td className="px-1.5 py-1">{z && mitWerten && !kind ? <ZustandMarke z={z} /> : null}</td>
        <td className="whitespace-nowrap px-1.5 py-1 text-right">
          {z && darfPflegen && (
            <span className="inline-flex items-center gap-0.5 text-muted-foreground">
              {!kind && z.state === 'OPEN' && mitWerten && !z.children.length && z.timeEntries === 0 && onTeilen && (
                <button type="button" tabIndex={-1} className="p-0.5 hover:text-primary" aria-label="teilen" title="Teilen (Teillieferung)" onMouseDown={(e) => e.preventDefault()} onClick={onTeilen}>
                  <Scissors className="h-3.5 w-3.5" />
                </button>
              )}
              {onEbeneWechseln && (
                <button
                  type="button"
                  tabIndex={-1}
                  className="p-0.5 hover:text-primary"
                  aria-label={kind ? 'ausrücken' : 'einrücken'}
                  title={kind ? 'Zur Hauptposition machen (Alt+←)' : 'Zur Unterposition der Zeile darüber machen (Alt+→)'}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onEbeneWechseln}
                >
                  {kind ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              )}
              {onSchieben && (
                <>
                  <button type="button" tabIndex={-1} className="p-0.5 hover:text-primary" aria-label="nach oben" title="Nach oben (Alt+↑)" onMouseDown={(e) => e.preventDefault()} onClick={() => onSchieben(-1)}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" tabIndex={-1} className="p-0.5 hover:text-primary" aria-label="nach unten" title="Nach unten (Alt+↓)" onMouseDown={(e) => e.preventDefault()} onClick={() => onSchieben(1)}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              {(z.state === 'OPEN' || z.state === 'NOT_BILLABLE') && !z.deliveryNote && onLoeschen && (
                <button type="button" tabIndex={-1} className="p-0.5 hover:text-destructive" aria-label="löschen" onMouseDown={(e) => e.preventDefault()} onClick={onLoeschen}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          )}
        </td>
      </tr>

      {zweiteZeile && (
        // Wie am Beleg (N-97): Die linken Zellen reichen über beide Zeilen, die
        // zweite beginnt an der Artikelspalte — hier trägt sie, was nur der
        // Verkauf kennt.
        <tr className={`border-b border-border align-top ${toenung} ${abgeblendet} ${ziellinie === 'unten' ? linieUnten : ''}`}>
          <td colSpan={spaltenGesamt - 6} className="px-1.5 pb-1">
            <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
              {/* „Verrechnung ab" steht immer da, auch eingeklappt (Auftraggeber 20.9.2026) — seit 9.10.2026
                  hier statt in einer eigenen Spalte; ab 1.462 px Listenbreite wieder in der Spalte der
                  Hauptzeile (Nachtrag 15, N15-16), dann fehlt es hier. Wie die übrigen Felder der zweiten
                  Zeile liegt es nicht in der Aktivierreihenfolge: Tab führt durch die Zeile, nicht durch den
                  Ausnahmefall (N-97). */}
              {!breit && (
                <label className="block w-36" title="Ab wann die Zeile verrechnet werden darf. Leer heißt: sofort möglich.">
                  {zusatzLabel('Verrechnung ab')}
                  <input type="date" tabIndex={-1} data-feld="billableFrom" className={`${zelle} text-xs ${grau}`} {...felder.billableFrom} />
                </label>
              )}
              {zusatzOffen && (
                <>
                  {/* „ausgeliefert am" steht hier immer (Auftraggeber 20.9.2026). Mit
                      Belegwesen schreibt es der Lieferschein — dann lehnt der Server
                      die Eingabe ab (`SALES_LINE_DELIVERED_BY_DOCUMENT`), und der
                      Hinweis sagt, warum. */}
                  <label className="block w-36" title={handLieferung ? undefined : 'Mit dem Belegwesen setzt der Lieferschein diesen Tag.'}>
                    {zusatzLabel('ausgeliefert am')}
                    <input type="date" tabIndex={-1} className={`${zelle} text-xs`} {...felder.deliveredAt} />
                  </label>
                  <label className="block min-w-[10rem] flex-1">
                    {zusatzLabel('Interne Notiz (steht nie auf dem Beleg)')}
                    <input tabIndex={-1} className={`${zelle} text-xs`} {...felder.internalNote} />
                  </label>
                </>
              )}
            </div>
            {zusatzOffen && z && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {SALES_LINE_ORIGIN_LABELS[z.origin]}
                {z.timeEntries > 0 && ` · ${z.timeEntries} Buchungen`}
                {' · angelegt '}
                {alsTag(z.createdAt)}
                {z.createdBy ? ` von ${z.createdBy.name}` : ''}
                {z.updatedBy && ` · zuletzt geändert ${alsTag(z.updatedAt ?? z.createdAt)} von ${z.updatedBy.name}`}
              </p>
            )}
          </td>
        </tr>
      )}
    </tbody>
  )
}

/**
 * Das Geldfeld gibt Text zurück; der Speicherbaustein erwartet ihn über `onChange`.
 * Ein kleines Ereignis genügt — `useFeld` liest nur `target.value`.
 */
function geldGetippt(feld: Eingabe, text: string) {
  feld.onChange({ target: { value: text } } as unknown as ChangeEvent<HTMLInputElement>)
}
